<template>
  <div
    class="pane-form"
    :class="{ 'is-editing': isEditing }"
  >
    <VisualTextEditor
      v-model:value="newPrompt"
      data-locator="add-prompt-textarea"
      :clean-content-on-blur="false"
      :error-messages="newPromptErrors"
      hide-menubar
      :max-length="NEW_PROMPT_MAX_LENGTH"
      :placeholder="placeholder"
      clean-content-on-validation
      keep-toolbar-on-blur
      counter="footer"
      :counter-label="$t('common.characters')"
      :disabled="disabled"
      ref="visualTextEditor"
      cleanPasteStyle
      @blur="onBlur"
      @focus="onFocus"
    />

    <div class="pane-form__footer d-flex align-center px-2 py-2">
      <div class="pane-form__footer-left d-flex align-center">
        <AppTooltip
          :disabled="disabled"
          location="top"
          data-locator="ai-ask-doc-open-documents-picker-tooltip"
        >
          <template #activator="{ props }">
            <app-button
              v-if="isAskDoc"
              v-bind="props"
              data-locator="ai-ask-doc-open-documents-picker"
              type="icon"
              size="small"
              variant="text"
              class="pane-form__plus-button"
              :disabled="disabled"
              @click="onOpenDocumentsPicker"
            >
              <app-material-icon
                name="add"
                color="#375576"
                size="sm"
              />
            </app-button>
          </template>

          {{ $t('common.ai-assistant.AIAssistantPaneForm.selectDocuments') }}
        </AppTooltip>

        <div
          v-if="selectedDocuments.length > 0"
          class="pane-form__sources"
          data-locator="ai-ask-doc-selected-documents"
        >
          <v-menu
            v-model="isSourcesMenuOpen"
            :close-on-content-click="false"
            location="top start"
            offset="8"
            content-class="pane-form__sources-menu"
          >
            <template #activator="{ props: menuProps }">
              <AppTooltip
                location="top"
                data-locator="ai-ask-doc-selected-documents-tooltip"
              >
                <template #activator="{ props: tooltipProps }">
                  <button
                    v-bind="{ ...menuProps, ...tooltipProps }"
                    type="button"
                    class="pane-form__sources-pill rounded-pill"
                    data-locator="ai-ask-doc-selected-documents-pill"
                  >
                    <div class="d-flex align-center ga-2 min-w-0">
                      <app-material-icon
                        name="description"
                        size="sm"
                        color="#005E9E"
                      />
                      <span class="pane-form__sources-label">
                        {{ $t('common.ai-assistant.AIAssistantPaneForm.multipleSources', { count: selectedDocuments.length }) }}
                      </span>
                    </div>
                  </button>
                </template>

                {{ $t('common.ai-assistant.AIAssistantPaneForm.multipleSourcesTooltip') }}
              </AppTooltip>
            </template>

            <div class="pane-form__sources-menu-content">
              <div
                v-for="({ doc, key, isDeleted }, index) in sourceItems"
                :key="key"
                class="pane-form__source-item"
                :class="{ 'pane-form__source-item--deleted': isDeleted }"
              >
                <div class="pane-form__source-item-left">
                  <ClosdFildersIcon
                    :document="doc"
                    :size="18"
                    class="pane-form__source-item-file-icon"
                  />

                  <AppTooltip location="top">
                    <template #activator="{ props: tooltipProps }">
                      <span
                        v-bind="tooltipProps"
                        class="pane-form__source-item-name"
                      >
                        {{ doc.name || doc.basename }}
                      </span>
                    </template>

                    <template v-if="isDeleted">
                      {{ $t('common.ai-assistant.AIAssistantPaneForm.deletedDocumentTooltip') }}
                    </template>
                    <template v-else>
                      {{ doc.name || doc.basename }}
                    </template>
                  </AppTooltip>
                </div>

                <AppTooltip
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <app-button
                      v-bind="tooltipProps"
                      type="icon"
                      size="x-small"
                      variant="text"
                      class="pane-form__source-item-remove"
                      @click.stop="removeSelectedDocument(index)"
                    >
                      <app-material-icon
                        name="close"
                        color="#375576"
                        size="sm"
                      />
                    </app-button>
                  </template>

                  {{ $t('common.ai-assistant.AIAssistantPaneForm.removeDocument') }}
                </AppTooltip>
              </div>
            </div>
          </v-menu>
        </div>
      </div>

      <div class="ml-auto d-flex align-center">
         <v-divider class="my-auto mx-1" vertical style="height: 20px;" />

        <AppTooltip
          location="top"
          :disabled="!shouldShowSelectDocumentHint"
          data-locator="ai-ask-doc-post-prompt-tooltip"
        >
          <template #activator="{ props }">
            <span v-bind="props" class="post-prompt-button-wrapper">
              <app-button
                data-locator="post-prompt-button"
                :disabled="!canPostPrompt"
                :loading="disabled"
                class="post-prompt-button"
                size="small"
                type="icon"
                @click="onPostPrompt"
              >
                <app-material-icon name="arrow_upward" size="xs" color="currentColor" />
              </app-button>
            </span>
          </template>

          {{ $t('common.ai-assistant.AIAssistantPaneForm.selectAtLeastOneDocument') }}
        </AppTooltip>
      </div>
    </div>

    <SharedDocumentsPickerDialog
      v-if="isAskDoc"
      v-model="localSelectedDocuments"
      :is-open="isDocumentsDialogOpen"
      :allowed-types="['file']"
      :max-selection="pickerMaxSelection"
      @update:isOpen="onDocumentsPickerOpenChange"
      @confirm="onConfirmDocuments"
    />
  </div>
</template>

<script>
import SharedDocumentsPickerDialog from '@/common/documents-picker/SharedDocumentsPickerDialog.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { getPlainText } from '@/common/helpers/htmlHelpers'
import { escapeHtml } from '@/common/utils/strings'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { AIAssistantQuestionTypes } from '@/types/ai-assistant'

import {
  canPostPrompt,
  documentKey,
  isDeletedDocument,
  liveDocuments,
  ASK_DOC_MAX_DOCUMENTS,
  NEW_PROMPT_MAX_LENGTH,
} from './aiAssistantPaneFormHelpers'
import AppTooltip from '../AppTooltip.vue'
const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPaneForm'

export default {
  name: 'AIAssistantPaneForm',
  components: {
    VisualTextEditor,
    SharedDocumentsPickerDialog,
    ClosdFildersIcon,
    AppTooltip,
  },
  emits: ['data-unsaved', 'post-prompt', 'update:selectedDocuments', 'draft-content-changed', 'sources-confirmed'],
  props: {
    cleared: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    questionType: {
      type: String,
      required: true,
    },
    selectedDocuments: {
      type: Array,
      default: () => ([]),
    },
    maxDocuments: {
      type: Number,
      default: ASK_DOC_MAX_DOCUMENTS,
    },
  },
  data: () => ({
    NEW_PROMPT_MAX_LENGTH,
    TRANSLATION_KEY_PREFIX,
    isEditing: false,
    newPrompt: '',
    isDocumentsDialogOpen: false,
    isSourcesMenuOpen: false,
    localSelectedDocuments: [],
    // The picker writes its selection through v-model on every click: this flag tells the close
    // handler whether that selection was confirmed (keep it) or cancelled (revert it).
    justConfirmedDocuments: false,
  }),
  computed: {
    isReadonly () {
      return this.disabled
    },
    isAskDoc () {
      return this.questionType === AIAssistantQuestionTypes.ASK_DOC
    },
    canPostPrompt () {
      return canPostPrompt({
        disabled: this.disabled,
        prompt: this.newPrompt,
        isAskDoc: this.isAskDoc,
        selectedDocumentsCount: this.localSelectedDocuments.length,
        maxDocuments: this.maxDocuments,
      })
    },
    shouldShowSelectDocumentHint () {
      return this.isAskDoc && !this.disabled && this.localSelectedDocuments.length === 0
    },
    // 0 = no limit for the documents picker (backend sends -1 when there is no limit)
    pickerMaxSelection () {
      return Math.max(this.maxDocuments, 0)
    },
    sourceItems () {
      return this.selectedDocuments.map((doc, index) => ({
        doc,
        key: documentKey(doc, index),
        isDeleted: isDeletedDocument(doc),
      }))
    },
    // Deleted documents cannot be re-selected in the picker, but they stay in the pill until the
    // next prompt is sent: the back-end then detaches them and they are gone after a reload.
    deletedDocuments () {
      return this.selectedDocuments.filter(doc => isDeletedDocument(doc))
    },
    newPromptLength () {
      return escapeHtml(this.newPrompt).length
    },
    newPromptIsTooLong () {
      return this.newPromptLength > this.NEW_PROMPT_MAX_LENGTH
    },
    newPromptErrors () {
      const errors = []

      if (this.newPromptIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    placeholder () {
      if (this.isReadonly) {
        return ''
      }
      if (this.questionType === AIAssistantQuestionTypes.ASK_DOC) {
        return this.$t(`${TRANSLATION_KEY_PREFIX}.newPromptPlaceholderAskDoc`)
      }
      return this.$t(`${TRANSLATION_KEY_PREFIX}.newPromptPlaceholder`)
    },
  },
  watch: {
    newPrompt () {
      this.emitDraftContentChange()
    },
    cleared (value) {
      if (value) {
        this.onClear()
      }
    },
    selectedDocuments: {
      immediate: true,
      deep: true,
      handler (value) {
        this.localSelectedDocuments = liveDocuments(Array.isArray(value) ? value : [])

        if (!(value || []).length) {
          this.isSourcesMenuOpen = false
        }

        this.emitDraftContentChange()
      },
    },
  },
  methods: {
    onClear () {
      this.$refs.visualTextEditor.clear()
    },
    onBlur () {
      this.toggleEditMode(false)
    },
    onFocus () {
      this.toggleEditMode(true)
    },
    onPostPrompt () {
      if (!this.canPostPrompt) return
      this.$emit('post-prompt', this.newPrompt)
      this.$refs.visualTextEditor.clear()
      this.emitDraftContentChange()
    },
    emitDraftContentChange () {
      const plainText = getPlainText(this.newPrompt)
      const hasDraft = plainText.length > 0 || this.selectedDocuments.length > 0
      this.$emit('draft-content-changed', hasDraft)
    },
    toggleEditMode (isEditing) {
      if (this.isReadonly) return
      this.isEditing = isEditing
    },
    onOpenDocumentsPicker () {
      if (this.disabled) return
      this.localSelectedDocuments = liveDocuments(this.selectedDocuments)
      this.isDocumentsDialogOpen = true
    },
    // Cancelling the picker must not keep the documents ticked while it was open: the picker is a
    // controlled component, so restoring the confirmed selection is up to this form.
    onDocumentsPickerOpenChange (isOpen) {
      this.isDocumentsDialogOpen = isOpen

      if (!isOpen && !this.justConfirmedDocuments) {
        this.localSelectedDocuments = liveDocuments(this.selectedDocuments)
      }

      this.justConfirmedDocuments = false
    },
    onConfirmDocuments (documents) {
      this.justConfirmedDocuments = true

      let sanitized = (documents || []).filter(doc => doc?.type === 'file')
      if (this.maxDocuments > 0) {
        sanitized = sanitized.slice(0, this.maxDocuments)
      }
      const nextSelection = [...sanitized, ...this.deletedDocuments]

      this.localSelectedDocuments = sanitized
      this.$emit('update:selectedDocuments', nextSelection)
      this.$emit('sources-confirmed', nextSelection)
    },
    removeSelectedDocument (index) {
      const nextSelection = this.selectedDocuments.filter((_, itemIndex) => itemIndex !== index)

      this.localSelectedDocuments = liveDocuments(nextSelection)
      this.$emit('update:selectedDocuments', nextSelection)

      if (!nextSelection.length) {
        this.isSourcesMenuOpen = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pane-form {
  outline: 1px solid rgb(var(--v-theme-grey-lighten-4));
  background-color: white;
  border-radius: 8px;
  overflow: hidden;

  &.is-editing {
    outline: 2px solid rgb(var(--v-theme-info-darken-1));
  }

  &.is-read-only {
    outline: 1px solid rgb(var(--v-theme-grey-200));
  }

  :deep(.editor__content .ProseMirror) {
    border: none !important;
    max-height: 100px;
  }
  :deep(.editor__menubar) {
    border: none !important;
    padding: 4px 0;
  }

  :deep(.editor__content .ProseMirror p.is-editor-empty:first-child::before) {
    color: #5E7997;
  }

  :deep(.text-counter) {
    bottom: 40px;
    right: 70px;
    position: absolute;
    color: #2A476A;
  }

  &:not(.is-editing) :deep(.text-counter) {
    opacity: 0;
  }

  :deep(.editor__errors) {
    display: none;
  }
}

.pane-form__footer {
  min-height: 44px;
  gap: 8px;
}

.pane-form__footer-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.pane-form__plus-button {
  border-radius: 100%;
  color: #375576 !important;
}

.pane-form__sources {
  display: flex;
  align-items: center;
  min-width: 0;
}

.pane-form__sources-pill {
  height: 32px;
  padding: 0 10px;
  background: #DAE9FC;
  color: #005E9E;
}

.pane-form__sources-label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

:deep(.pane-form__sources-menu) {
  box-shadow: 0 8px 24px rgba(0, 23, 46, 0.16);
  border: 1px solid #DFE3E8;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.pane-form__sources-menu-content {
  min-width: 260px;
  max-width: 320px;
  padding: 8px;
}

.pane-form__source-item {
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 6px;
  padding: 4px 6px;
  transition: background 0.25s ease;
}

.pane-form__source-item:hover {
  background: #00172E0D;
}

.pane-form__source-item-left {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pane-form__source-item-file-icon {
  flex: 0 0 auto;
}

.pane-form__source-item--deleted .pane-form__source-item-name {
  color: rgb(var(--v-theme-text-disabled));
}

.pane-form__source-item-name {
  min-width: 0;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #2A476A;
  font-size: 13px;
  cursor: default;
}

.pane-form__source-item-remove {
  min-width: 20px !important;
  width: 20px;
  height: 20px;
  color: #EDF0F2 !important;
  flex: 0 0 auto;
  border-radius: 50%;
  background-color: #EDF0F2 !important;
}

.post-prompt-button-wrapper {
  display: inline-flex;
}

.post-prompt-button {
  border-radius: 100%;

  &:disabled {
    background-color: #EDF0F2;
    color: #5E7997;
  }
  &:not(:disabled), &:focus {
    background-color: rgb(var(--v-theme-purple-darken-1));
    color: white !important;

    &:hover, &:active {
      background-color: rgb(var(--v-theme-purple-darken-4));
    }
  }
}
</style>
